/**
 * Regular (scored) mode progression — pure timers / rolls.
 * ArcadeGame owns application (spawns, ammo, caps). See docs/regular-play-mode.md.
 */

export const REGULAR_INTRO_ENEMY_START = 10;
export const REGULAR_INTRO_ENEMY_END = 20;
export const REGULAR_INTRO_ALLY_START = 20;
export const REGULAR_INTRO_ALLY_END = 25;
export const REGULAR_LOOT_START = 30;
export const REGULAR_WAVE_START = 38;
export const REGULAR_WAVE_CLEAR_DELAY = 3;
export const REGULAR_DUAL_BLOCK_PERIOD = 45;
export const REGULAR_DUAL_BLOCK_DURATION = 5;

export interface RegularDirectorState {
  /** Uniform random in [10, 20). */
  introEnemyAt: number;
  /** Uniform random in [20, 25). */
  introAllyAt: number;
  spawnedIntroEnemy: boolean;
  spawnedIntroAlly: boolean;
  /**
   * When `elapsed` reaches this, spawn next wave (set to elapsed+3 after a clear).
   * null while enemies are alive or waves not yet armed.
   */
  nextWaveAt: number | null;
}

export interface RegularBlockLoot {
  rail: number;
  missile: number;
  chaff: number;
  /** Total ally units to try to add (may be clamped by live cap). */
  allies: number;
}

export function createRegularDirectorState(): RegularDirectorState {
  return {
    introEnemyAt:
      REGULAR_INTRO_ENEMY_START +
      Math.random() * (REGULAR_INTRO_ENEMY_END - REGULAR_INTRO_ENEMY_START),
    introAllyAt:
      REGULAR_INTRO_ALLY_START +
      Math.random() * (REGULAR_INTRO_ALLY_END - REGULAR_INTRO_ALLY_START),
    spawnedIntroEnemy: false,
    spawnedIntroAlly: false,
    nextWaveAt: null,
  };
}

/**
 * Max simultaneous obstacle cubes for Regular.
 * t < 30 → 2; t ≥ 30 → 1, except 5s dual windows every 45s after t ≥ 38.
 */
export function regularObstacleLimit(elapsed: number): number {
  if (elapsed < REGULAR_LOOT_START) {
    return 2;
  }
  if (elapsed < REGULAR_WAVE_START) {
    return 1;
  }
  const local = (elapsed - REGULAR_WAVE_START) % REGULAR_DUAL_BLOCK_PERIOD;
  return local < REGULAR_DUAL_BLOCK_DURATION ? 2 : 1;
}

/** Independent rolls per block break (t ≥ 30 only — caller gates). */
export function rollRegularBlockLoot(): RegularBlockLoot {
  let allies = 0;
  if (Math.random() < 1 / 12) {
    allies += 1;
  }
  if (Math.random() < 1 / 15) {
    allies += 2;
  }
  return {
    rail: Math.random() < 1 / 3 ? 30 : 0,
    missile: Math.random() < 1 / 8 ? 1 : 0,
    chaff: Math.random() < 1 / 10 ? 1 : 0,
    allies,
  };
}

/** Uniform pick among wave sizes 2, 3, 4. */
export function pickRegularWaveSize(): 2 | 3 | 4 {
  const sizes = [2, 3, 4] as const;
  return sizes[Math.floor(Math.random() * sizes.length)] ?? 2;
}
